import { createContext } from 'react';
import { useMatch } from 'react-router-dom';

import { SelectOption } from '@components/selectmulti';
import { TranslationMap, getScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';
import { formatTime } from '@utils/strings';

import { TravellerActionGroupAction } from 'api/traveller-actions/requests';
import {
  JourneyAction,
  JourneyActionValuesTypes,
  TravellerJourneyAction,
  TravellerJourneyListAction,
  TravellerJourneyListItemAction,
} from 'api/types';
import { useCachedWorkspaces } from 'api/workspaces/hooks';
import { Workspace } from 'api/workspaces/requests';
import { newDateIgnoreLocalTimezone } from 'ui/actions/timezone';

export const TestIdContext = createContext({} as Record<JourneyAction['shortId'], string>);

export function getWaitFixedDescriptionText(
  values: JourneyActionValuesTypes['wait_until_fixed_date']
) {
  const { t } = getScopedTranslate(nodeLocale);
  if (!values?.date) {
    return t('descriptionFixed', {
      date: '<Selected date>',
      time: '<Selected time>',
    });
  }

  const date = newDateIgnoreLocalTimezone(values.date.value);
  const hour = date.getHours();
  const minute = date.getMinutes();

  return t('descriptionFixed', {
    date: abbreviateDate(date, 'en', 'date-short-fancy-no-year'),
    time: `${hour}:${minute < 10 ? '0' + minute : minute}`,
  });
}

export function getWaitRelativeDescriptionText(
  values: JourneyActionValuesTypes['wait_until_relative_date'],
  locale: TranslationMap
): string {
  const { t } = getScopedTranslate(locale);
  const comparator = values?.comparator;
  const variables = values?.referenceDate?.variables || {};
  const referenceDate = variables[Object.keys(variables)[0]]?.label ?? undefined;
  const days = values?.days;
  const hour = values?.hour;
  const minute = values?.minute;
  const time = formatTime(hour?.value, minute?.value);
  if (!referenceDate || !hour || !minute) {
    return t('configureAction');
  } else if (comparator?.value === 'lt') {
    return t('descriptionBefore', {
      referenceDate: referenceDate,
      daysBefore: days?.value ?? '0',
      time,
    });
  } else if (comparator?.value === 'gt') {
    return t('descriptionAfter', {
      referenceDate: referenceDate,
      daysAfter: days?.value ?? '0',
      time,
    });
  } else {
    return t('descriptionExact', {
      referenceDate: referenceDate,
      time,
    });
  }
}

export function getWaitRelativeDescriptionJourneyText(
  values: JourneyActionValuesTypes['wait_until_relative_date']
): string {
  return getWaitRelativeDescriptionText(values, nodeLocale);
}

export const getWaitUntilDescription = (values: JourneyActionValuesTypes['wait_for_time']) => {
  const { t } = getScopedTranslate(nodeLocale);
  const translateProps = {
    daysCount: values?.days?.value?.toString() ?? '0',
    hoursCount: values?.hours?.value?.toString() ?? '0',
    minutesCount: values?.minutes?.value?.toString() ?? '0',
  };
  return t('description', translateProps);
};

export function getActionStatus(
  action:
    | TravellerJourneyAction
    | TravellerActionGroupAction
    | TravellerJourneyListAction
    | TravellerJourneyListItemAction
): 'completed' | 'in_progress' | 'not_started' | 'error' | 'skipped' {
  if (action.result?.dateSucceeded) {
    return 'completed';
  } else if (action.status === 'skipped') {
    return 'skipped';
  } else if (action.result?.dateStarted) {
    return 'in_progress';
  } else if (action.result?.dateFailed) {
    return 'error';
  }
  return 'not_started';
}

export function getActionRatio(
  action:
    | TravellerJourneyAction
    | TravellerActionGroupAction
    | TravellerJourneyListAction
    | TravellerJourneyListItemAction
): number {
  const status = getActionStatus(action);
  return status === 'completed' ? 1 : status === 'in_progress' ? 0.5 : 0;
}

export const companyTravellerAttributePrefix = 'company_traveller_attribute__';
export const journeyAttributePrefix = 'journey_attribute__';

const nodeLocale = {
  en: {
    descriptionBefore: 'Trigger {daysBefore} days before {referenceDate} at {time}',
    descriptionAfter: 'Trigger {daysAfter} days after {referenceDate} at {time}',
    descriptionExact: 'Trigger on {referenceDate} at {time}',
    descriptionFixed: 'Trigger on • {date} at {time}',
    configureAction: 'Configure action',
    description:
      'Trigger next action after {daysCount} days, {hoursCount} hours, {minutesCount} minutes',
  },
};

export const getShortIdToDescriptiveMap = (actions: JourneyAction[]) => {
  const typeCounter = {} as Record<JourneyAction['action'], number>;
  const descriptiveMap = {} as Record<JourneyAction['shortId'], string>;
  actions.forEach((action) => {
    if (!typeCounter[action.action]) {
      typeCounter[action.action] = 1;
    } else {
      typeCounter[action.action] += 1;
    }
    descriptiveMap[action.shortId] = `${action.action}-${typeCounter[action.action]}`;
  });

  return descriptiveMap;
};

export const getMultiOptionValuesArray = (values: any[]): SelectOption['value'][] => {
  return values.filter((opt: SelectOption) => 'value' in opt).map((opt: SelectOption) => opt.value);
};

export const useCurrentWorkspace = () => {
  const params = useMatch('/workspaces/:workspaceId/*')?.params;
  const workspaceId = params?.workspaceId ?? '';
  const workspaces = useCachedWorkspaces();
  const currentWorkspace =
    workspaces.find((workspace) => workspace.shortId === workspaceId) ??
    ({
      workspaceId,
      shortId: workspaceId,
      title: '',
      description: '',
      icon: 'IconWorkspace',
      color: 'green100',
      dateCreated: '',
      dateUpdated: '',
      isPrivate: false,
      members: [],
    } as Workspace);
  return { workspaceId, ...currentWorkspace };
};

export const useWorkspaceMap = () => {
  const workspaces = useCachedWorkspaces();
  return workspaces.reduce((acc, workspace) => {
    acc[workspace.shortId] = workspace;
    return acc;
  }, {} as Record<string, Workspace>);
};
